import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@reactuses/core";
import { useQuery } from "@tanstack/react-query";
import { useBlockWindowQuery } from "../../../table/useBlockWindowQuery";
import { useServerTable } from "../../../table/tableFeatures";
import { usePersistedTableState } from "../../../table/usePersistedTableState";
import { RT8ColumnSelector } from "../../../core/blocks/MetricSelector";
import { SearchInput } from "../../../fields/base/TextField";
import { RT8Pagination } from "../../../table/Pagination";
import { Table } from "../../../table/Table";
import { idsToVisibilityState, resetTable, selectionStateToSelectedIds } from "../../../table/utils";
import { RT8FilterWidget } from "../../../../modules/merchandising/components/tables/FilterWidget";
import { Button } from "../../../core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "../../merchandise/constants";
import { BulkActionButton } from "../../../table/BulkActionButton";
import { createColumnBuilder } from "../../../table/columnBuilder";
import { ProductCell } from "../../../../modules/merchandising/components/cells/ProductCell";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import { HStack, VStack, Wrap } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import AdvertisementProductExpander from "../../../../modules/advertising/components/AdvertisementProductExpander";
import {
  type ProductAdV4Row,
  useGetV4ProductAdReport,
} from "../../../../modules/perf_reports/api/product_ad/v4ProductAdReport";
import { useV4ProductAdReportExport } from "../../../../modules/perf_reports/api/product_ad/v4ProductAdReportExport";
import { DownloadV4Report } from "../../../../modules/perf_reports/DownloadReport";
import { Switch } from "../../../fields/base/Switch";
import { useUpdateProductAdState } from "../../../../modules/advertising/api/useUpdateProductAdState";
import { AD_STATES, AD_STATUSES, PRODUCT_AD_ENABLED, PRODUCT_AD_PAUSED } from "../../../core/blocks/AdPlan/constants";
import { PRODUCT_AD_SERVING_STATUS_OPTIONS } from "../../../../modules/advertising/filters/productLifecycleFilters";
import { useGetV4ProductReportOptions } from "../../../../modules/perf_reports/api/product/v4ProductReportOptions";
import { useTagFilterOptions } from "../../../../modules/tags/api/useTagFilterOptions";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { snakeCaseToSentenceCase, toTitleCase } from "../../../../utils/formatText";
import { TableErrorWrapper } from "../../../table/TableErrorWrapper";
import { useSimpleCan } from "../../../../config/casl/casl";
import { AdvertisementCell } from "../../../../modules/advertising/tableCells/AdvertisementCell";
import { DataViewPicker } from "../../../../modules/dataViews/components/DataViewPicker";
import NiceModal from "@ebay/nice-modal-react";
import { BulkAddProductsToAdsModal } from "../../../../modules/advertising/components/BulkAddProductsToAdsModal";

const columnHelper = createColumnBuilder<ProductAdV4Row>();

const SERVING_STATUS_SELECTORS = Object.fromEntries(PRODUCT_AD_SERVING_STATUS_OPTIONS.map((o) => [o.value, o.label]));

// Composite, so a bulk action can read the ids it needs off a selection made on any page.
const ROW_ID_SEPARATOR = ":";

const makeRowId = (row: ProductAdV4Row) =>
  [row.product_advertisement_id, row.gvadvertisement_id, row.gvproduct_id].join(ROW_ID_SEPARATOR);

const parseRowId = (rowId: string) => {
  const [, advertisementId, productId] = rowId.split(ROW_ID_SEPARATOR);
  const ids = { advertisementId: Number(advertisementId), productId: Number(productId) };
  return Number.isInteger(ids.advertisementId) && Number.isInteger(ids.productId) ? ids : undefined;
};

type ProductAdsTableProps = {
  adId?: number;
  productId?: number;
  /** Show ad_name column linking to ad details. Useful for account-wide view. Default: false */
  showAdColumn?: boolean;
  showProductColumn?: boolean;
  /** Key for persisting table state. Default: "adProductTable" */
  persistKey?: string;
  showAddProductAdsModal?: boolean;
};

const ProductAdsTable = ({
  adId,
  productId,
  showAdColumn = false,
  showProductColumn = true,
  persistKey = "adProductTable",
  showAddProductAdsModal = false,
}: ProductAdsTableProps) => {
  const { t } = useTranslation(["advertising", "common"]);
  const standardMetricIds = useStandardEntityMetrics("productAd");
  const stateMutation = useUpdateProductAdState();
  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const userCanExportProductAds = useSimpleCan("export", "ProductAd");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const columns = useMemo(
    () => [
      ...(showProductColumn ?
        [
          columnHelper.accessor("product_title", {
            header: () => <span>{t(($) => $.advertising["Product"])}</span>,
            enableHiding: false,
            minSize: 100,
            size: 600,
            cell: (props) => {
              const { product_title } = props.row.original;

              return (
                <ProductCell
                  original={{
                    ...props.row.original,
                    product_id: props.row.original.gvproduct_id.toString(),
                    cellContext: "merchandising",
                  }}
                  value={product_title}
                  showInventoryBadge={false}
                  className={css({ marginRight: "auto", overflowX: "hidden" })}
                />
              );
            },
            meta: {
              columnFilterOptions: {
                label: t(($) => $.advertising["Product Title"]),
              },
            },
          }),
        ]
      : []),
      // Ad column - only shown in account-wide view
      ...(showAdColumn ?
        [
          columnHelper.accessor("ad_name", {
            id: "ad_name",
            header: t(($) => $.advertising["Advertisement"]),
            size: 250,
            cell: ({ getValue, row }) => {
              const adName = getValue();
              const adId = row.original.gvadvertisement_id;
              return (
                <AdvertisementCell
                  ad={{
                    ad_id: adId,
                    ad_name: adName,
                    ad_type: row.original.ad_type,
                    ad_status: row.original.ad_status,
                    state: row.original.state,
                  }}
                />
              );
            },
            meta: {
              columnSelectorOptions: {
                grouping: "configuration",
              },
            },
          }),
        ]
      : []),
      // Invisible columns to support filtering
      columnHelper.accessor((row) => row, {
        id: "inventory_status",
        header: t(($) => $.advertising["Inventory State"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "sales_state",
        header: t(($) => $.advertising["Sales State"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: tagsEnhancedEnabled ? "tags_enhanced.id" : "tags",
        header: t(($) => $.advertising["Tags"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select",
          },
        },
      }),

      columnHelper.accessor((row) => row, {
        id: "variations",
        header: t(($) => $.advertising["Variants"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "ad_status",
        header: t(($) => $.advertising["Ad Status"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "state",
        header: t(($) => $.advertising["Ad State"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      // Invisible columns end
      columnHelper.accessor("user_status", {
        id: "user_status",
        header: t(($) => $.advertising["State"]),
        enableResizing: false,
        size: 100,
        cell: function StateCell({ getValue, row }) {
          const value = getValue() === "enabled";
          const inlineStateMutation = useUpdateProductAdState();

          // Updating cache from mutation is a PITA, since query key is super complicated. Let's just use a local state
          const [switchState, setSwitchState] = useState(value);
          if (value !== switchState && !inlineStateMutation.isPending) {
            setSwitchState(value);
          }

          return (
            <Switch
              isSelected={switchState}
              isPending={inlineStateMutation.isPending}
              isDisabled={!userCanUpdateAds}
              onChange={(isSelected) => {
                inlineStateMutation.mutate(
                  {
                    custom_api_request: "pause_product_ad",
                    products: [row.original.gvproduct_id],
                    id: row.original.gvadvertisement_id,
                    state: isSelected ? PRODUCT_AD_ENABLED : PRODUCT_AD_PAUSED,
                  },
                  {
                    onSuccess: () => {
                      setSwitchState(isSelected);
                    },
                  },
                );
              }}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
        },
      }),
      columnHelper.accessor("serving_status", {
        id: "serving_status",
        header: t(($) => $.advertising["Serving Status"]),
        size: 70,
        cell: ({ getValue }) => {
          const status = getValue();
          if (!status) return t(($) => $.advertising["Pending"]);
          const selector = SERVING_STATUS_SELECTORS[status];
          if (selector) return t(selector);
          return <>{snakeCaseToSentenceCase(status)}</>;
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
    ],
    [standardMetricIds, userCanUpdateAds, showAdColumn, showProductColumn, tagsEnhancedEnabled, t],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "ad_name",
    "user_status",
    "serving_status",
    "acos",
    "ad_sales",
    "ad_spend",
    "impressions",
    "clicks",
    "orders",
    "ctr",
    "conversion_rate",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey,
    initialState: {
      columnVisibility: defaultHiddenCols,
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        start: ["product_title", "ad_name"],
        end: [],
      },
      sorting: [{ id: "sales", desc: true }],
    },
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);

  const byProductAdOptionsGetter = useGetV4ProductAdReport(adId, productId);

  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());
  const productAdByProductAdQuery = useBlockWindowQuery(byProductAdOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debounceSearch,
  });

  const tagEnhancedOptions = useTagFilterOptions("PRODUCT", { enabled: tagsEnhancedEnabled });
  const tagOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.advertising["All Tags"]) },
      ...(productReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [productReportOptionsQuery.data?.data.tags, t],
  );

  const variantOptions = useMemo(() => {
    const { variations } = productReportOptionsQuery.data?.data ?? {};

    if (!variations) {
      return [];
    }

    return [
      { value: "all", label: t(($) => $.advertising["All Variants"]) },
      ...Object.keys(variations).flatMap((variationCategory) => {
        return (
          variations[variationCategory]?.map((variation) => ({
            label: `${toTitleCase(variationCategory)}: ${variation}`,
            value: { [variationCategory]: variation },
          })) ?? []
        );
      }),
    ];
  }, [productReportOptionsQuery.data?.data, t]);

  const formattedData = useMemo(() => {
    if (!productAdByProductAdQuery.data?.data) {
      return [];
    }
    return productAdByProductAdQuery.data.data.map((row) => {
      return {
        ...row,
        product_id: row.gvproduct_id,
        advertisement_id: row.gvadvertisement_id,
        total_cost: row.cost,
        total_cost_prev: row.cost_prev,
      };
    });
  }, [productAdByProductAdQuery.data?.data]);

  const table = useServerTable({
    data: formattedData,
    columns,
    getRowCanExpand: () => true,
    getRowId: makeRowId,
    enableRowSelection: userCanUpdateAds,
    rowCount: productAdByProductAdQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    alternateSortingEnabled: true,
    meta: {
      showSkeleton:
        productAdByProductAdQuery.isFetching &&
        (!productAdByProductAdQuery.data || productAdByProductAdQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        inventory_status: [
          {
            value: "all",
            label: t(($) => $.advertising["All Inventory States"]),
            description: t(($) => $.advertising["Show products with any inventory state"]),
          },
          ...INVENTORY_STATE_OPTIONS.filter(Boolean),
        ],
        sales_state: [
          {
            value: "all",
            label: t(($) => $.advertising["All Sales States"]),
          },
          ...SALE_STATE_OPTIONS.filter((option) => option !== undefined),
        ],
        serving_status: [
          {
            value: "all",
            label: t(($) => $.advertising["All Statuses"]),
          },
          ...PRODUCT_AD_SERVING_STATUS_OPTIONS,
        ],
        ad_status: [{ value: "all", label: t(($) => $.advertising["All Ad Statuses"]) }, ...Object.values(AD_STATUSES)],
        state: [
          { value: "all", label: t(($) => $.advertising["All Ad States"]) },
          AD_STATES.ACTIVE,
          AD_STATES.PAUSE,
          AD_STATES.ENDED,
        ],
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        variations: variantOptions.filter(Boolean),
        user_status: [
          {
            value: "all",
            label: t(($) => $.advertising["All Statuses"]),
          },
          {
            value: "enabled",
            label: t(($) => $.advertising["Enabled"]),
          },
          {
            value: "paused",
            label: t(($) => $.advertising["Paused"]),
          },
        ],
      },
    },
  });

  const exportMutation = useV4ProductAdReportExport(state.columnFilters, adId, productId);
  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  const groupSelectedProductsByAd = () => {
    return selected.reduce<Record<number, number[]>>((acc, rowId) => {
      const ids = parseRowId(rowId);
      if (ids) {
        (acc[ids.advertisementId] ??= []).push(ids.productId);
      }
      return acc;
    }, {});
  };

  const updateSelectedProductAdStates = (productAdState: number) => {
    Object.entries(groupSelectedProductsByAd()).forEach(([adIdKey, products]) => {
      stateMutation.mutate({
        custom_api_request: "pause_product_ad",
        products,
        id: Number(adIdKey),
        state: productAdState,
      });
    });
  };

  return (
    <div>
      {userCanUpdateAds && (
        <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"} gap={"large"}>
          {showAddProductAdsModal && adId && (
            <Button
              onPress={() => {
                void NiceModal.show(BulkAddProductsToAdsModal, {
                  advertisementIds: [adId],
                  renderAdEligibilityWarning: false,
                });
              }}
            >
              {t(($) => $.advertising["Add Product Ads"])}
            </Button>
          )}
          <BulkActionButton
            selected={selected}
            options={[
              {
                label: t(($) => $.advertising["Pause Product Advertisements"], { count: selected.length }),
                action: () => {
                  updateSelectedProductAdStates(PRODUCT_AD_PAUSED);
                },
                confirmationMessage: (
                  <p>
                    {t(($) => $.advertising["Are you sure you want to pause the selected products?"], {
                      count: selected.length,
                    })}
                  </p>
                ),
              },
              {
                label: t(($) => $.advertising["Enable Product Advertisements"], { count: selected.length }),
                action: () => {
                  updateSelectedProductAdStates(PRODUCT_AD_ENABLED);
                },
                confirmationMessage: (
                  <p>{t(($) => $.advertising["Are you sure you want to enable the selected products?"])}</p>
                ),
              },
            ]}
          />
        </Wrap>
      )}
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button variant={"contentOnly"} onPress={() => resetTable(table, persistKey)} fontSize={"xxlarge"}>
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey={persistKey} datasetKey="product_ad" table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <TableErrorWrapper
        error={productAdByProductAdQuery.error}
        userMessage={t(($) => $.advertising["Error loading stats"])}
      >
        <Table
          table={table}
          className={css({ minWidth: "100%" })}
          renderExpander={(row) => (
            <AdvertisementProductExpander row={row.original} productAdId={row.original.product_advertisement_id} />
          )}
        />
      </TableErrorWrapper>
      {userCanExportProductAds && (
        <HStack width={"100%"} padding={"small"}>
          <DownloadV4Report downloadMutation={exportMutation} />
        </HStack>
      )}
    </div>
  );
};

export default ProductAdsTable;
